<i18n locale="en">
{
  "actions": {
    "delete": "Delete",
    "edit": "Edit"
  },
  "add-label": "Add Review",
  "reviews-limit-reached": "Reviews limit reached. Delete a review before you can add another.",
  "locked": {
    "title": "Reviews are managed by the SEO team",
    "body": "Featured reviews are selected automatically. Contact the SEO team to change them."
  }
}
</i18n>

<template>
  <EditPanel
    :is-editing="
      selectedIndex !== null &&
      currentBlockParams.reviews[selectedIndex] !== undefined
    "
  >
    <template #preview>
      <EditorLayout>
        <div class="reviews-editor flex-col gap-6">
          <div
            v-if="!reviewsEditable"
            class="flex-col items-start gap-2"
            data-testid="reviews-editor-locked-notice"
          >
            <OwnType :text="t('locked.title')" variant="title-regular" />
            <OwnType
              :text="t('locked.body')"
              variant="paragraph-small"
              color="secondary"
            />
          </div>

          <Reorderable
            v-if="reviewsEditable"
            v-slot="{ item, index }"
            v-model="currentBlockParams.reviews"
            item-key="comment"
            class="space-y-3"
            @reorder="onReorder"
          >
            <OwnOption
              :data-testid="`review-row-${index}`"
              @click="onEdit(index)"
            >
              <div class="flex-row gap-4">
                <DragHandle />
                <OwnType
                  class="flex-1"
                  color="primary"
                  variant="title-regular"
                  :text="item.name"
                />
                <OwnActionMenu
                  :actions="actions"
                  @action="onAction($event, index)"
                >
                  <template #trigger>
                    <PhDotsThreeCircle class="text-color-secondary" size="24" />
                  </template>
                </OwnActionMenu>
              </div>
            </OwnOption>
          </Reorderable>

          <div v-else class="space-y-3">
            <OwnOption
              v-for="(item, index) in currentBlockParams.reviews"
              :key="index"
              el="div"
              :data-testid="`review-row-readonly-${index}`"
            >
              <OwnType
                color="primary"
                variant="title-regular"
                :text="item.name"
              />
            </OwnOption>
          </div>

          <div v-if="reviewsEditable" class="flex-col gap-2">
            <OwnButton
              :disabled="currentBlockParams.reviews.length >= maxReviews"
              :text="t('add-label')"
              data-testid="add-review-button"
              @click="onNew"
            />
            <p
              v-if="currentBlockParams.reviews.length >= maxReviews"
              class="text-paragraph-small text-color-secondary"
              v-text="t('reviews-limit-reached')"
            ></p>
          </div>
        </div>

        <template #actions>
          <slot />
        </template>
      </EditorLayout>
    </template>
    <template #edit>
      <ReviewDetailEditor
        class="reviews-editor__child"
        :review="currentBlockParams.reviews[selectedIndex]"
        @back="onBack"
        @update="onUpdate"
      />
    </template>
  </EditPanel>
</template>

<script>
import {
  PhDotsThreeCircle,
  PhPencilSimpleLine,
  PhTrash,
} from '@phosphor-icons/vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { mapGetters } from 'vuex'

import {
  REVIEWS_EDITABLE_FLAG,
  REVIEWS_RESTRICTION_FLAG,
} from '@/features/website-builder/gating'
import EditorLayout from '@/lib/builder/public/editor/EditorLayout.vue'
import EditPanel from '@/lib/builder/public/editor/EditPanel.vue'
import { OwnActionMenu } from '@/ui/actions/OwnActionMenu'
import { OwnButton } from '@/ui/OwnButton'
import { OwnOption } from '@/ui/OwnOption'
import { OwnType } from '@/ui/OwnType'
import DragHandle from '@/ui/reorderable/DragHandle.vue'
import Reorderable from '@/ui/reorderable/Reorderable.vue'
import { useReactiveFeatureFlag } from '@/utils/useFeatureFlags'

import { ReviewsCommands } from '../commands/ReviewsCommands'

import ReviewDetailEditor from './ReviewDetailEditor'

export default {
  name: 'ReviewsEditor',
  components: {
    DragHandle,
    EditPanel,
    EditorLayout,
    OwnActionMenu,
    OwnButton,
    OwnOption,
    OwnType,
    PhDotsThreeCircle,
    Reorderable,
    ReviewDetailEditor,
  },
  props: {
    /**
     * Id getting passed to the editor component
     */
    id: { type: String, required: true },
  },
  data() {
    return {
      selectedIndex: null,
    }
  },
  setup() {
    const { t } = useI18n()
    const { enabled: restrictionEnabled } = useReactiveFeatureFlag(
      REVIEWS_RESTRICTION_FLAG
    )
    const { enabled: isSeoOpsEditor } = useReactiveFeatureFlag(
      REVIEWS_EDITABLE_FLAG
    )

    // Restriction off ⇒ the block edits exactly as it always has, for everyone.
    // Only once it is on does editing narrow to the SEO ops allowlist.
    const reviewsEditable = computed(
      () => !restrictionEnabled.value || isSeoOpsEditor.value
    )

    return { reviewsEditable, t }
  },
  computed: {
    ...mapGetters({
      blockById: 'websiteBuilder/blockById',
    }),
    actions() {
      return [
        {
          icon: PhPencilSimpleLine,
          value: 'edit',
        },
        {
          color: 'danger',
          icon: PhTrash,
          value: 'delete',
        },
      ].map((action) => ({
        ...action,
        label: this.t(`actions.${action.value}`),
      }))
    },
    currentBlock() {
      const { id } = this

      return this.blockById(id)
    },
    currentBlockParams() {
      const { currentBlock } = this

      return currentBlock?.params
    },
    maxReviews() {
      return 12
    },
  },
  methods: {
    async onAction(action, index) {
      switch (action) {
        case 'delete':
          await ReviewsCommands.deleteReview(this.currentBlock, index)
          break
        case 'edit':
          this.onEdit(index)
          break
      }
    },
    onBack() {
      this.selectedIndex = null
    },
    onEdit(index) {
      this.selectedIndex = index
    },
    async onNew() {
      this.selectedIndex = await ReviewsCommands.createReview(this.currentBlock)
    },
    async onReorder({ newIndex, oldIndex }) {
      await ReviewsCommands.reorderReview(this.currentBlock, oldIndex, newIndex)
    },
    async onUpdate(updatedReview) {
      await ReviewsCommands.updateReview(
        this.currentBlock,
        this.selectedIndex,
        updatedReview
      )
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/styles/globals';

.reviews-editor {
  &__child {
    width: calc(100% - 8px);

    @include globals.flyout-shadow();
  }
}
</style>
